<template>
	<nav
		ref="navContainer"
		class="nav-main"
		:class="isMobile ? 'nav-main--mobile-search' : 'nav-main--desktop overflow-hidden'"
		@keydown.down.prevent="$emit('pass-focus', $event)"
	>
		<div class="flex items-center" :class="{ 'gap-3' : isMobile && !hideExpandAndCollapse }">
			<ul v-show="!isSearchVisible && !isMobile" ref="navButtonContainer" class="nav-main__tabs flex gap-3 w-100 flex-nowrap bg-shade-2" :class="{ 'text-button--aligned': !isMobile, 'nav-main__tabs--no-words': noRoomForWords, 'nav-main__tabs--no-badges': noRoomForBadgeNumbers, 'nav-main__tabs-payment': showCognitoPay }">
				<li class="nav-main__tab" :class="isSelected('home') || isSelected('forms') ? 'nav-main__tab--selected' : null">
					<CToolTip
						placement="top"
						:disabled="isMobile || !noRoomForWords"
						:use-button="false"
					>
						<template #button>
							<CIconButton
								:text="resources.Homepage"
								:to="homeRoute"
								:tabindex="isSelected('home') ? '-1' : '0'"
								icon="home"
								:intense="false"
								data-cf-id="home.nav"
								@click.native="handleHomeButtonClick"
							/>
						</template>
						<span>{{ resources.Homepage }}</span>
					</CToolTip>
				</li>
				<li class="nav-main__tab nav-main__tab--tasks" :class="isSelected('task-dashboard') ? 'nav-main__tab--selected' : null">
					<CToolTip
						placement="top"
						:disabled="isMobile || !noRoomForWords"
						:use-button="false"
					>
						<template #button>
							<CIconButton
								icon="tasks"
								:to="taskRoute"
								:intense="false"
								:tabindex="isSelected('task-dashboard') ? '-1' : '0'"
								@click="handleTasksButtonClick"
							>
								<div class="relative">
									{{ resources.Tasks }}
									<CBadge v-if="isPaidPlan && taskCount > 0" :text="getAbbreviatedNumber(taskCount)" type="alert" />
								</div>
							</CIconButton>
						</template>
						<span>{{ resources.Tasks }}</span>
					</CToolTip>
				</li>
				<li v-if="isOrgAdmin && showCognitoPay" class="nav-main__tab nav-main__tab--payment" :class="{ 'nav-main__tab--selected': isCognitoPaySelected }">
					<CToolTip
						placement="top"
						:disabled="isMobile || !noRoomForWords"
						:use-button="false"
					>
						<template #button>
							<CIconButton
								icon="credit-card"
								:to="hasOnboardedCognitoPayAccount ? cognitoPayRoute : null"
								:intense="false"
								:tabindex="isCognitoPaySelected ? '-1' : '0'"
								:data-cf-id="hasOnboardedCognitoPayAccount ? 'cognito-pay.nav-dashboard' : 'cognito-pay.nav-onboarding'"
								@click="$emit('payment-clicked')"
							>
								<div class="relative">
									Payment
									<CBadge v-if="disputeCount > 0" :text="getAbbreviatedNumber(disputeCount)" type="alert" />
								</div>
							</CIconButton>
						</template>
						<span>Payment</span>
					</CToolTip>
				</li>
				<li v-if="isOrgAdmin" class="nav-main__tab">
					<CToolTip
						placement="top"
						:disabled="isMobile || !noRoomForWords"
						:use-button="false"
					>
						<template #button>
							<CIconButton text="Settings" :to="settingsRoute" icon="settings" :intense="false" tabindex="0" class="bg-shade-2" />
						</template>
						<span>Settings</span>
					</CToolTip>
				</li>
			</ul>
			<template v-if="isSearchVisible || isMobile">
				<CNavSearch
					class="nav-main__search w-100"
					:class="{ 'pr-3' : !isMobile }"
					@search="setSearchQuery"
					@blur="handleBlur"
					@focus="handleFocus"
				/>
			</template>
			<div class="nav-main__icon-buttons flex items-center">
				<CToolTip
					v-if="!isSearchVisible && !hideExpandAndCollapse && !currentQuery"
					placement="top"
					class="nav-main__icon-expand-collapse"
					:class="{ '-mr-2' : isMobile}"
					:disabled="isMobile"
					:use-button="false"
				>
					<template #button>
						<CIconButton
							tabindex="0"
							:icon="expandCollapseOption ==='Expand' ? 'plus-square' : 'minus-square'"
							:aria-label="expandCollapseOption ==='Expand' ? 'Expand All' : 'Collapse All'"
							:intense="false"
							@click="emitExpandCollapse({expandCollapseOption})"
						/>
					</template>
					<span>{{ expandCollapseOption }}</span>
				</CToolTip>
				<CToolTip
					v-if="allowUserToSearch && (!isMobile || isSearchVisible)"
					class="search-button-tooltip"
					:class="isMobile ? '-mr-2' : '-mr-3'"
					data-search-button
					placement="top"
					:use-button="false"
				>
					<template #button>
						<CIconButton
							tabindex="0"
							:icon="isSearchVisible ? 'ex' : 'search'"
							:intense="false"
							:aria-label="isSearchVisible ? 'Close Search' : 'Search'"
							class="search-button pointer"
							data-search-button
							@click="handleSearchClicked"
						/>
					</template>
					<span class="flex items-center gap-3">{{ isSearchVisible ? 'Close Search' : 'Search' }} <kbd v-if="!isSearchVisible" class="inline-block bg-shade-3 border border-shade-4 rounded px-2 py-1 leading-none">/</kbd></span>
				</CToolTip>
			</div>
		</div>
		<CUpsellDialog
			:show="showTasksUpsellDialog"
			feature="tasks"
			:org-code="orgCode"
			:is-org-owner="isOrgOwner"
			@close="showTasksUpsellDialog = false"
		/>
	</nav>
</template>

<script lang="ts">
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CUpsellDialog from 'src/components/dialogs/UpsellDialog.vue';
	import CNavSearch from './NavSearch.vue';
	import { getAbbreviatedNumber } from '@cognitoforms/utils/utilities';
	import type { PropType } from 'vue';
	import { computed, inject, ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
	import { isMobileKey, localizationResourcesKey } from 'src/utilities/injection-keys';
	import CToolTip from 'src/components/interaction/ToolTip.vue';
	import { useRoute } from 'vue-router/composables';
	import debounce from 'src/utilities/debounce';
	import { ClearSearchEvent, useEventBus } from 'src/views/admin/utils/useEventBus';
	import session from '@cognitoforms/api/services/session';
	import { notifyHomeNavClicked } from '@cognitoforms/api/services/page-history-service';

	export default {
		name: 'CNavMain',
		components: {
			CIconButton,
			CBadge,
			CUpsellDialog,
			CToolTip,
			CNavSearch
		},
		props: {
			isOrgOwner: { type: Boolean, required: true },
			isOrgAdmin: { type: Boolean, required: true },
			orgCode: { type: String, required: true },
			isPaidPlan: { type: Boolean, required: true },
			canUseTasks: { type: Boolean, required: true },
			taskCount: { type: Number, required: true },
			expandCollapseOption: { type: String as PropType<'Expand' | 'Collapse'>, default: 'Collapse' },
			hideExpandAndCollapse: { type: Boolean, default: false },
			allowUserToSearch: { type: Boolean, default: true },
			searchVisible: { type: Boolean, default: false },
			emitExpandCollapse: { type: Function, default: () => {} },
			disputeCount: { type: Number, default: 0 },
			hasOnboardedCognitoPayAccount: { type: Boolean, default: false },
			hasIncompleteCognitoPayAccount: { type: Boolean, default: false }
		},
		emits: ['search-query', 'expand-collapse', 'pass-focus', 'close-search', 'payment-clicked'],
		setup(props, { emit }) {
			const isMobile = inject(isMobileKey);
			const resources = inject(localizationResourcesKey);

			const route = useRoute();
			const homeRoute = computed(() => `/${props.orgCode}/home`);
			const settingsRoute = computed(() => `/${props.orgCode}/settings/general`);
			const taskRoute = computed(() => (props.canUseTasks) ? `/${props.orgCode}/tasks` : '');

			const showCognitoPay = computed(() => {
				return session.value?.enabledFeatures?.CognitoPay && (props.hasOnboardedCognitoPayAccount || props.hasIncompleteCognitoPayAccount);
			});
			const cognitoPayRoute = computed(() => `/${props.orgCode}/payment/analytics`);

			const isSearchVisible = ref(props.searchVisible);
			const navButtonContainer = ref<HTMLElement | null>(null);
			const navContainer = ref<HTMLElement | null>(null);
			const taskButton = ref(false);
			const noRoomForWords = ref(false);
			const noRoomForBadgeNumbers = ref(false);

			const showTasksUpsellDialog = ref(false);
			const handleTasksButtonClick = () => {
				if (!taskRoute.value)
					showTasksUpsellDialog.value = true;
			};

			const handleHomeButtonClick = () => {
				notifyHomeNavClicked().catch(() => {});
			};

			const currentQuery = ref('');
			// search query logic
			const fireSearchEvent = debounce((query: string) => emit('search-query', query), 300);
			const setSearchQuery = (query: string) => {
				currentQuery.value = query.toLowerCase();
				fireSearchEvent(currentQuery.value);
			};

			const { emit: emitClearSearchEvent } = useEventBus(ClearSearchEvent);

			const handleSearchClicked = () => {
				if (isSearchVisible.value) {
					setSearchQuery('');
					emit('close-search');
					if (isMobile.value) {
						emitClearSearchEvent();
						isSearchVisible.value = false;
						return;
					}
				}
				isSearchVisible.value = !isSearchVisible.value;
			};

			watch(() => props.searchVisible, (val) => {
				if (val)
					handleSearchClicked();
			});

			const handleBlur = (e) => {
				// if the blur is clicking the x on the outside, we let handleSearchClick handle it
				if (!e || (!e.relatedTarget?.hasAttribute('data-search-button') && currentQuery.value === '')) {
					isSearchVisible.value = false;
					emit('close-search');
				}
			};

			const handleFocus = () => {
				if (isMobile.value)
					isSearchVisible.value = true;
			};

			const isSelected = (name) => {
				return route.name === name;
			};

			const isCognitoPaySelected = computed(() => {
				return route.name && (route.name as string).startsWith('payment-');
			});

			onMounted(() => {
				const resizeObserver = new ResizeObserver(debounce(() => {
					noRoomForWords.value = false;
					noRoomForBadgeNumbers.value = false;
					nextTick(() => {
						noRoomForWords.value = navContainer.value.getBoundingClientRect().right - navButtonContainer.value.querySelector('li:last-child').getBoundingClientRect().right < 90;
						// Wait after checking for words to be removed to see if there's room for the task number
						nextTick(() => {
							noRoomForBadgeNumbers.value = noRoomForWords.value && navContainer.value.getBoundingClientRect().right - navButtonContainer.value.querySelector('li:last-child').getBoundingClientRect().right < 90;
						});
					});
				}, 100));

				if (navButtonContainer.value)
					resizeObserver.observe(navButtonContainer.value);
				onUnmounted(() => resizeObserver.disconnect());
			});

			return {
				isMobile,
				resources,
				handleTasksButtonClick,
				handleHomeButtonClick,
				getAbbreviatedNumber,
				homeRoute,
				settingsRoute,
				showTasksUpsellDialog,
				taskRoute,
				showCognitoPay,
				cognitoPayRoute,
				isSelected,
				isCognitoPaySelected,
				isSearchVisible,
				setSearchQuery,
				handleSearchClicked,
				handleBlur,
				handleFocus,
				navContainer,
				navButtonContainer,
				taskButton,
				noRoomForWords,
				noRoomForBadgeNumbers,
				currentQuery
			};
		}
	};
</script>

<style lang="scss">
.nav-main {
	@include b-down($b9) {
		// tighter space here than elsewhere
		--space__touch-target: 10px;
	}

	&__tabs {
		z-index: $z1;
	}

	&--desktop {
		padding-top: 2px;

		& > div {
			max-height: 38px;
		}

		@container (max-width: 250px) {

			.nav-main__tabs-payment ~ .nav-main__icon-buttons .nav-main__icon-expand-collapse {
				display: none;
			}
		}

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container (max-width: 300px) {

			:root & .nav-main__tabs {
				gap: $space__2;
			}

			:root & .nav-main__tabs-payment {
				gap: $space__1;
			}
		}

		.nav-main__icon-buttons {
			padding-bottom: 3px;
		}

		.nav-main__tab {
			position: relative;
			white-space: nowrap;

			a.router-link-active {
				background-color: transparent;
				box-shadow: none;
			}

			a,
			button {
				margin-bottom: 5px;
				gap: 0;
			}

			&::after {
				position: absolute;
				bottom: 0;
				left: 5px;
				width: calc(100% - 10px);
				height: 3px;
				pointer-events: none;
				content: '';
			}

			&--selected {
				font-weight: $weight__text--bold;

				.i-button__icon {
					color: var(--border-icon-selected);
				}

				&::after {
					background-color: var(--border-selected-nav-intense);
				}
			}
		}

		.i-button__text {
			margin-left: $space__3;
		}

		.badge {
			margin-left: $space__2;
		}

		.i-button--no-text {
			padding: var(--space__touch-target);
		}

		@include b-down($b9) {

			.i-button--no-text {
				margin-left: -2px;
			}

			.nav-main__icon-buttons {
				margin-right: -3px;
			}

			.nav-main__tabs--no-words {
				margin-left: -3px;
			}
		}

		.nav-main__tabs--no-words {

			.i-button__text {
				display: none;
			}

			.nav-main__tab--tasks,
			.nav-main__tab--payment {

				.i-button__text {
					display: inline;
					margin-left: 0;
					visibility: hidden;
					font-size: 0;
				}

				span {
					visibility: visible;
				}
			}

			@include b-down($b9) {

				:root & {
					gap: 0;
				}
			}

			.i-button {
				border-radius: 50px;
				margin-bottom: 3px;
				padding: var(--space__touch-target);

				:root & {
					box-shadow: none;
				}
			}
		}

		.nav-main__tabs--no-badges {

			.nav-main__tab--tasks,
			.nav-main__tab--payment {

				a {
					gap: 0;
				}

				.badge {
					display: block;
					position: absolute;
					right: -6px;
					top: -10px;
					width: 6px;
					min-width: 0;
					height: 6px;
					padding: 0;

					span {
						display: none;
					}
				}
			}
		}
	}
}
</style>